import {
  AccessibleMultiSelectDropdown,
  AccessibleSelectDropdown,
  ChakraBox,
  ChakraFlex,
  ChakraHStack,
  EquipUIFireflyV2Theme,
  FAIconName,
  FireflyButton,
  FireflyIconButton,
  InputGroupV2,
  ModalV2,
  Text,
  TextInputV2,
} from '@equip.health/ui';
import type {
  AccessibleDropdownHandle,
  AccessibleSelectOption,
} from '@equip.health/ui';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { CarePlanResponse } from '~/lib/types/carePlan/carePlans';
import { isTerminalGoalStatus } from '~/lib/types/carePlan/CarePlanGoalStatus';
import {
  useGetInterventionCategoriesEnumQuery,
  useGetInterventionCategoriesAssigneesMapQuery,
} from '~/store/services/refCarePlanEnums';
import { useCreateCustomInterventionsMutation } from '~/store/services/carePlan';
import Loading from '~/pages/loading';
import useErrorToast from '~/lib/hooks/useErrorToast';
import { GlobalErrorCode } from '~/lib/constants/global.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { CarePlanEvents } from '~/analytics/carePlanEvents';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';

const { neutral } = EquipUIFireflyV2Theme.colors;
const TOP_LEVEL_ID = 'add-custom-intervention-modal';

// equip-ui's TextInputV2 renders its <input> with id `${id}__text-input-v2__input`.
// Blur first so focus can move even when the current active element is inside a portal.
const focusFieldById = (targetId: string) => {
  (document.activeElement as HTMLElement | null)?.blur?.();
  void Promise.resolve().then(() => {
    document.getElementById(`${targetId}__text-input-v2__input`)?.focus();
  });
};

type InterventionFormData = {
  id: string;
  description: string;
  selectedGoalIds: string[];
  selectedCategoryCode: string | null;
  selectedProviderTypes: string[];
};

type InterventionRefs = {
  category: AccessibleDropdownHandle | null;
  goals: AccessibleDropdownHandle | null;
  providers: AccessibleDropdownHandle | null;
};

type Props = {
  cancelLabel?: string;
  carePlan: CarePlanResponse;
  leftContent?: ReactNode;
  noteExternalId?: string;
  onClose: () => void;
  preselectedGoalId?: string;
  saveLabel?: string;
  subHeading?: string;
  title?: string;
  treatmentEpisodeId: string;
};

export const AddCustomInterventionModal = ({
  cancelLabel,
  carePlan,
  leftContent,
  noteExternalId,
  onClose,
  preselectedGoalId,
  saveLabel,
  subHeading,
  title,
  treatmentEpisodeId,
}: Props) => {
  const { showErrorToast } = useErrorToast();
  const { trackEvent } = useAnalytics();
  const { currentUser } = useGlobalContext();

  const currentUserRoleCode =
    currentUser?.providerTypeDetails?.code ||
    (currentUser?.providerTypeDetails?.name
      ? currentUser.providerTypeDetails.name.toUpperCase().replaceAll(' ', '_')
      : null);

  const [interventions, setInterventions] = useState<InterventionFormData[]>(
    () => [
      {
        id: crypto.randomUUID(),
        description: '',
        selectedGoalIds: preselectedGoalId ? [preselectedGoalId] : [],
        selectedCategoryCode: null,
        selectedProviderTypes: [],
      },
    ],
  );

  const dropdownRefs = useRef<Map<string, InterventionRefs>>(new Map());
  const getOrCreateRefs = (id: string): InterventionRefs => {
    const existing = dropdownRefs.current.get(id);
    if (existing) return existing;
    const fresh: InterventionRefs = {
      category: null,
      goals: null,
      providers: null,
    };
    dropdownRefs.current.set(id, fresh);
    return fresh;
  };

  const {
    data: interventionCategories,
    isLoading: isInterventionCategoriesLoading,
    isError: isInterventionCategoriesError,
  } = useGetInterventionCategoriesEnumQuery();
  const {
    data: assigneesMap,
    isLoading: isAssigneesMapLoading,
    isError: isAssigneesMapError,
  } = useGetInterventionCategoriesAssigneesMapQuery();

  const [createCustomInterventions, { isLoading: isCreating }] =
    useCreateCustomInterventionsMutation();

  useEffect(() => {
    if (isInterventionCategoriesError || isAssigneesMapError) {
      showErrorToast(
        GlobalErrorCode.CREATE_CUSTOM_INTERVENTIONS,
        500,
        'Failed to load intervention reference data',
      );
    }
  }, [isInterventionCategoriesError, isAssigneesMapError, showErrorToast]);

  const goalOptions: AccessibleSelectOption[] = useMemo(
    () =>
      carePlan.problems.flatMap((problem) =>
        problem.goals
          .filter((goal) => !isTerminalGoalStatus(goal.status.code))
          .map((goal) => ({
            label: `${goal.pathwayGoal.label} (${problem.pathwayProblem.label})`,
            value: goal.id,
          })),
      ),
    [carePlan.problems],
  );

  const interventionCategoryOptions: AccessibleSelectOption[] = useMemo(() => {
    if (!interventionCategories) return [];
    return [...interventionCategories]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((cat) => ({
        label: cat.label,
        value: cat.code,
      }));
  }, [interventionCategories]);

  const addIntervention = () => {
    trackEvent(
      CarePlanEvents.clickOnAddInterventionButtonInAddCustomInterventionModal,
    );
    setInterventions((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        description: '',
        selectedGoalIds: [],
        selectedCategoryCode: null,
        selectedProviderTypes: [],
      },
    ]);
  };

  const removeIntervention = (id: string) => {
    trackEvent(
      CarePlanEvents.clickOnRemoveInterventionButtonInAddCustomInterventionModal,
    );
    dropdownRefs.current.delete(id);
    setInterventions((prev) => prev.filter((item) => item.id !== id));
  };

  const updateIntervention = (
    id: string,
    updates: Partial<InterventionFormData>,
  ) => {
    setInterventions((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...updates } : item)),
    );
  };

  const getProviderOptionsForInterventionCategory = (
    categoryCode: string | null,
  ): AccessibleSelectOption[] => {
    if (!categoryCode || !assigneesMap) return [];
    const assignees = assigneesMap[categoryCode] || [];
    return [...assignees]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((assignee) => ({
        label: assignee.label,
        value: assignee.code,
      }));
  };

  // Default the assignee to the current user's role when it is a valid INDIVIDUAL assignee for the chosen category.
  const defaultProviderTypesForInterventionCategory = (
    categoryCode: string | null,
  ): string[] => {
    if (!categoryCode || !currentUserRoleCode || !assigneesMap) return [];
    const targetCode = currentUserRoleCode.toUpperCase();
    const matchingAssignee = (assigneesMap[categoryCode] ?? []).find(
      (a) => a.type === 'INDIVIDUAL' && a.code.toUpperCase() === targetCode,
    );
    return matchingAssignee ? [matchingAssignee.code] : [];
  };

  const handleSave = async () => {
    try {
      const interventionsToCreate = interventions.map((intervention) => {
        if (!intervention.selectedCategoryCode) {
          throw new Error('Category code is required');
        }

        const assignees =
          assigneesMap?.[intervention.selectedCategoryCode] || [];
        const providerTypes: string[] = [];
        const providerTypeGroups: string[] = [];

        intervention.selectedProviderTypes.forEach((code) => {
          const assignee = assignees.find((a) => a.code === code);
          if (assignee?.type === 'INDIVIDUAL') {
            providerTypes.push(code);
          } else if (assignee?.type === 'GROUP') {
            providerTypeGroups.push(code);
          }
        });

        return {
          categoryCode: intervention.selectedCategoryCode,
          description: intervention.description,
          goalIds: intervention.selectedGoalIds,
          providerTypes,
          providerTypeGroups,
        };
      });

      trackEvent(
        CarePlanEvents.clickOnSaveToCarePlanButtonInAddCustomInterventionModal,
      );
      await createCustomInterventions({
        treatmentEpisodeId,
        carePlanId: carePlan.id,
        interventions: interventionsToCreate,
        noteExternalId: noteExternalId ?? null,
      }).unwrap();
      onClose();
    } catch (error: any) {
      showErrorToast(
        GlobalErrorCode.CREATE_CUSTOM_INTERVENTIONS,
        error?.status || 500,
        error?.message,
      );
    }
  };

  const isSaveDisabled = interventions.some(
    (intervention) =>
      !intervention.description.trim() ||
      intervention.selectedGoalIds.length === 0 ||
      !intervention.selectedCategoryCode ||
      intervention.selectedProviderTypes.length === 0,
  );

  const isLoading = isInterventionCategoriesLoading || isAssigneesMapLoading;
  const isError = isInterventionCategoriesError || isAssigneesMapError;

  return (
    <ModalV2
      id={TOP_LEVEL_ID}
      isOpen
      footerContent={
        !isLoading && !isError ? (
          <ChakraHStack justifyContent="space-between" width="100%">
            <FireflyButton
              id={`${TOP_LEVEL_ID}__cancel`}
              leftIcon={FAIconName.timesRegular}
              onClick={() => {
                trackEvent(
                  CarePlanEvents.clickOnCancelButtonInAddCustomInterventionModal,
                );
                onClose();
              }}
              variant="secondary"
            >
              {cancelLabel ?? 'Cancel'}
            </FireflyButton>
            <FireflyButton
              id={`${TOP_LEVEL_ID}__save`}
              isDisabled={isSaveDisabled}
              isLoading={isCreating}
              leftIcon={FAIconName.checkRegular}
              onClick={handleSave}
            >
              {saveLabel ?? 'Save to care plan'}
            </FireflyButton>
          </ChakraHStack>
        ) : (
          <ChakraHStack justifyContent="space-between" width="100%">
            <FireflyButton
              id={`${TOP_LEVEL_ID}__close`}
              leftIcon={FAIconName.timesRegular}
              onClick={onClose}
              variant="secondary"
            >
              Close
            </FireflyButton>
          </ChakraHStack>
        )
      }
      onClose={() => {
        trackEvent(CarePlanEvents.clickOnCloseIconInAddCustomInterventionModal);
        onClose();
      }}
      size="5xl"
      title={title ?? 'Add intervention'}
    >
      {subHeading && (
        <Text color="neutral.secondary" marginBottom="18px">
          {subHeading}
        </Text>
      )}
      {isLoading && <Loading />}
      {!isLoading && isError && (
        <Text color="critical.base">
          Unable to load intervention data. Please try again later.
        </Text>
      )}
      {!isLoading && !isError && (
        <ChakraFlex direction="row" gap="18px">
          {leftContent && (
            <ChakraBox
              border={`1px solid ${neutral.line}`}
              borderRadius="12px"
              flexShrink={0}
              padding="18px"
              width="240px"
            >
              {leftContent}
            </ChakraBox>
          )}
          <ChakraFlex direction="column" flex="1" gap="18px" minWidth="0">
            {interventions.map((intervention, index) => {
              const providerOptions = getProviderOptionsForInterventionCategory(
                intervention.selectedCategoryCode,
              );
              const refs = getOrCreateRefs(intervention.id);
              const isFirstCard = index === 0;
              const isLastCard = index === interventions.length - 1;
              const prevCardRefs = isFirstCard
                ? null
                : getOrCreateRefs(interventions[index - 1].id);

              return (
                <ChakraBox
                  key={intervention.id}
                  border={`1px solid ${neutral.line}`}
                  borderRadius="12px"
                  minWidth={0}
                  padding="18px"
                  width="100%"
                >
                  <ChakraFlex
                    alignItems="center"
                    gap="12px"
                    marginBottom="18px"
                  >
                    <ChakraBox flexShrink={0} width="180px">
                      <AccessibleSelectDropdown
                        autoFocusOnMount={isLastCard}
                        fieldName="type"
                        instanceId={intervention.id}
                        isClearable={false}
                        modalId={TOP_LEVEL_ID}
                        onSelectionChange={(next) => {
                          updateIntervention(intervention.id, {
                            selectedCategoryCode: next,
                            selectedProviderTypes:
                              defaultProviderTypesForInterventionCategory(next),
                          });
                        }}
                        onTabBackward={
                          isFirstCard
                            ? () => {
                                // Without an explicit onTabBackward, equip-ui's Tab handler lets the
                                // browser's Shift+Tab move focus — which lands on UniversalSelect's
                                // outer role="button" wrapper. Target the modal's close-X so the first card can escape.
                                document
                                  .querySelector<HTMLElement>(
                                    `[role="dialog"][id*="${TOP_LEVEL_ID}"] [data-testid="closeButton"]`,
                                  )
                                  ?.focus();
                              }
                            : () => prevCardRefs?.providers?.focus()
                        }
                        onTabForward={() => refs.goals?.focus()}
                        ref={(handle) => {
                          refs.category = handle;
                        }}
                        options={interventionCategoryOptions}
                        value={intervention.selectedCategoryCode}
                      />
                    </ChakraBox>
                    <Text>for</Text>
                    <ChakraBox flex="1 1 0" minWidth={0}>
                      <AccessibleMultiSelectDropdown
                        fieldName="problems"
                        instanceId={intervention.id}
                        modalId={TOP_LEVEL_ID}
                        onSelectionChange={(next) => {
                          updateIntervention(intervention.id, {
                            selectedGoalIds: next,
                          });
                        }}
                        onTabBackward={() => refs.category?.focus()}
                        onTabForward={() =>
                          focusFieldById(
                            `${TOP_LEVEL_ID}__description-input-${intervention.id}`,
                          )
                        }
                        ref={(handle) => {
                          refs.goals = handle;
                        }}
                        options={goalOptions}
                        value={intervention.selectedGoalIds}
                      />
                    </ChakraBox>
                    {interventions.length > 1 && (
                      <FireflyIconButton
                        ariaLabel={`Remove intervention ${index + 1}`}
                        color="neutral.tertiary"
                        icon={FAIconName.trashRegular}
                        id={`${TOP_LEVEL_ID}__remove-${intervention.id}`}
                        onClick={() => removeIntervention(intervention.id)}
                        variant="tertiary"
                      />
                    )}
                  </ChakraFlex>

                  <ChakraBox
                    marginBottom="18px"
                    onKeyDownCapture={(e: ReactKeyboardEvent) => {
                      if (e.key !== 'Tab') return;
                      e.preventDefault();
                      e.stopPropagation();
                      const active =
                        document.activeElement as HTMLElement | null;
                      active?.blur?.();
                      if (e.shiftKey) {
                        refs.goals?.focus();
                      } else {
                        refs.providers?.focus();
                      }
                    }}
                  >
                    <InputGroupV2
                      id={`${TOP_LEVEL_ID}__description-input-group-${intervention.id}`}
                    >
                      <TextInputV2
                        id={`${TOP_LEVEL_ID}__description-input-${intervention.id}`}
                        onChange={(value) =>
                          updateIntervention(intervention.id, {
                            description: value,
                          })
                        }
                        placeholder="Explain the recommended intervention"
                        value={intervention.description}
                      />
                    </InputGroupV2>
                  </ChakraBox>

                  {/* Provider Assignment */}
                  <ChakraBox>
                    <Text marginBottom="9px" variant="bodyMedium">
                      Assigned to
                    </Text>
                    <AccessibleMultiSelectDropdown
                      fieldName="role"
                      instanceId={intervention.id}
                      isDisabled={!intervention.selectedCategoryCode}
                      modalId={TOP_LEVEL_ID}
                      onSelectionChange={(next) => {
                        updateIntervention(intervention.id, {
                          selectedProviderTypes: next,
                        });
                      }}
                      onTabBackward={() =>
                        focusFieldById(
                          `${TOP_LEVEL_ID}__description-input-${intervention.id}`,
                        )
                      }
                      onTabForward={() => {
                        document
                          .getElementById(`${TOP_LEVEL_ID}__add-intervention`)
                          ?.focus();
                      }}
                      ref={(handle) => {
                        refs.providers = handle;
                      }}
                      options={providerOptions}
                      value={intervention.selectedProviderTypes}
                    />
                  </ChakraBox>
                </ChakraBox>
              );
            })}

            {/* Add intervention button */}
            <ChakraBox>
              <FireflyButton
                id={`${TOP_LEVEL_ID}__add-intervention`}
                leftIcon={FAIconName.plusRegular}
                onClick={addIntervention}
                variant="tertiary"
              >
                Add intervention
              </FireflyButton>
            </ChakraBox>
          </ChakraFlex>
        </ChakraFlex>
      )}
    </ModalV2>
  );
};
